import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  importProvidersFrom,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, switchMap, take, tap, EMPTY } from 'rxjs';
import { Router } from '@angular/router';
import { DialogModule } from '@angular/cdk/dialog';
import {
  MTM_AUTH_CLIENT,
  MTM_TRANSLATE_FN,
  MTM_ON_APP_TOKEN_READY,
  MTM_ON_LOGIN_SUCCESS,
  MTM_ON_TENANT_REQUIRED,
  MTM_NOTIFICATION_SERVICE,
  MTM_AUTH_CONFIG,
  AngularHttpAdapter,
  AngularNavigationAdapter,
  MtmAuthBridgeService,
  MtmNotificationService,
} from '@mtm/auth-ui-angular';
import { MtmAuthClient } from '@mtm/auth';
import type { Tenant } from '@mtm/auth';
import { provideStore, provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { appRoutes } from './app.routes';
import { appTokenInterceptor } from '@core/interceptors/app-token.interceptor';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { TenantContextService } from '@core/services/tenant-context.service';
import { registerTenantContext } from '@core/utils/tenant-key';
import { environment } from '@environments/environment';
import { TenantsEffects, tenantsFeature } from '@stores/tenants';
import { UiEffects, uiFeature } from '@stores/ui';
import { consoleFeature } from '@stores/console';
import { auditLogsFeature } from '@stores/audit-logs/audit-logs.feature';
import { AuditLogsEffects } from '@stores/audit-logs/audit-logs.effects';
import { dashboardFeature } from '@stores/dashboard/dashboard.feature';
import { DashboardEffects } from '@stores/dashboard/dashboard.effects';
import { AgentsEffects, agentsFeature } from '@stores/agents';
import { AiModelsEffects, aiModelsFeature } from '@stores/ai-models';
import { BusinessEntitiesEffects, businessEntitiesFeature } from '@stores/business-entities';
import { AiProvidersEffects, aiProvidersFeature } from '@stores/ai-providers';
import { PayloadsEffects, payloadsFeature } from '@stores/payloads';
import { RbacEffects, rbacFeature } from '@stores/rbac';
import { NotificationEffects } from '@core/effects/notification.effects';
import { createMtmTranslateFn } from '@core/i18n/mtm-auth-translate';

const initTranslations = (translate: TranslateService): (() => Promise<void>) => {
  return () => firstValueFrom(translate.use('en')).then(() => {});
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Initial navigation must stay non-blocking (the default): it then runs
    // after the app initializers, so bridge.initialize$() has restored the
    // session before any guard evaluates isAuthenticated(). With
    // withEnabledBlockingInitialNavigation() the router races the auth
    // initializer and F5 lands on /login despite a valid session.
    provideRouter(appRoutes, withComponentInputBinding()),

    // ── Sentry: capture unhandled errors + route tracing ──────────────────────
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),

    importProvidersFrom(DialogModule),
    provideHttpClient(withFetch(), withInterceptors([appTokenInterceptor, errorInterceptor])),
    provideTranslateService({ fallbackLang: 'en' }),
    provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    provideAppInitializer(() => initTranslations(inject(TranslateService))()),
    provideAppInitializer(() => {
      const bridge = inject(MtmAuthBridgeService);
      const tenantContext = inject(TenantContextService);
      registerTenantContext(tenantContext);
      // The SDK's defaults (`/`, `/login`, `/callback`, `/tenant-select`) miss this
      // app's callback paths, so without these the SSO/auth-process URL -- and its
      // one-time token -- would be stored as the returnUrl and re-entered on login.
      return bridge
        .initialize$([
          environment.authProcessUrl,
          environment.ssoCallbackUrl,
          environment.resetPasswordUrl,
        ])
        .pipe(tap(() => tenantContext.refresh()));
    }),

    // ── 1. MTM Auth client ───────────────────────────────────────────────────
    {
      provide: MTM_AUTH_CLIENT,
      useFactory: () => {
        const httpAdapter = inject(AngularHttpAdapter);
        const navigation = inject(AngularNavigationAdapter);
        return new MtmAuthClient({
          baseUrl: environment.iamApiUrl,
          appApiUrl: environment.appApiUrl,
          appSsoCallback: environment.ssoCallbackUrl,
          appResetPasswordCallback: environment.resetPasswordUrl,
          serviceUuid: environment.serviceUuid,
          ssoUrl: {
            google: environment.ssoUrl.google,
            azure: environment.ssoUrl.azure,
          },
          http: httpAdapter,
          navigation: navigation,
        });
      },
    },

    // ── 2. Translation function ──────────────────────────────────────────────
    // App dictionary first, SDK bundle second -- see createMtmTranslateFn. The old
    // `translate.instant(key)` alone left every `auth.authShell.*` key the sign-in
    // shell uses rendering as its raw dotted key, and dropped interpolation params.
    {
      provide: MTM_TRANSLATE_FN,
      useFactory: createMtmTranslateFn,
    },

    // ── 3. On login success: resolve tenant + exchange — no navigation ────────
    {
      provide: MTM_ON_LOGIN_SUCCESS,
      useFactory: () => {
        const router = inject(Router);
        const bridge = inject(MtmAuthBridgeService);
        const tenantContext = inject(TenantContextService);
        return () => {
          bridge
            .resolvePostLoginTenant$(environment.serviceUuid)
            .pipe(
              switchMap((result) => {
                if (result.autoSelected) {
                  return bridge.exchangeAuthCode$(result.authCode);
                }
                const returnUrl = bridge.popReturnUrl();
                router.navigate(['/tenant-select'], {
                  state: { tenants: result.tenants, returnUrl },
                });
                return EMPTY;
              }),
              take(1),
            )
            .subscribe({
              next: () => {
                tenantContext.refresh();
                router.navigate(['/tenants']);
              },
            });
        };
      },
    },

    // ── 4. Post auth-code exchange: navigate to tenants ─────────────────────
    {
      provide: MTM_ON_APP_TOKEN_READY,
      useFactory: () => {
        const router = inject(Router);
        const tenantContext = inject(TenantContextService);
        return () => {
          tenantContext.refresh();
          router.navigate(['/tenants']);
        };
      },
    },

    // ── 5. Cross-site session restore with multiple tenants ──────────────────
    {
      provide: MTM_ON_TENANT_REQUIRED,
      useFactory: () => {
        const router = inject(Router);
        const bridge = inject(MtmAuthBridgeService);
        return (tenants: Tenant[]) => {
          const returnUrl = bridge.popReturnUrl();
          router.navigate(['/tenant-select'], { state: { tenants, returnUrl } });
        };
      },
    },

    // ── 6. Notification service ──────────────────────────────────────────────
    {
      provide: MTM_NOTIFICATION_SERVICE,
      useExisting: MtmNotificationService,
    },

    // Where `MtmAuthRedirectComponent` sends the password-reset branch of a
    // callback. Its built-in default is `/auth/reset`, which is not this app's
    // path, so the emailed link would land on `**` -> /login.
    {
      provide: MTM_AUTH_CONFIG,
      useValue: {
        routes: {
          resetPassword: environment.resetPasswordUrl,
        },
      },
    },

    // ── NgRx ─────────────────────────────────────────────────────────────────
    provideStore(),
    provideState(uiFeature),
    provideState(consoleFeature),
    provideState(tenantsFeature),
    provideState(auditLogsFeature),
    provideState(dashboardFeature),
    provideState(agentsFeature),
    provideState(aiModelsFeature),
    provideState(businessEntitiesFeature),
    provideState(aiProvidersFeature),
    provideState(payloadsFeature),
    provideState(rbacFeature),
    provideEffects(
      TenantsEffects,
      UiEffects,
      AuditLogsEffects,
      DashboardEffects,
      AgentsEffects,
      AiModelsEffects,
      BusinessEntitiesEffects,
      AiProvidersEffects,
      PayloadsEffects,
      RbacEffects,
      NotificationEffects,
    ),
    importProvidersFrom(StoreDevtoolsModule.instrument({ maxAge: 25 })),
  ],
};
